import { useEffect, useState, type ReactElement } from 'react';
import { emberApi, isAuthRequired, type WeeklyLetter } from '../api';
import { trackFailed } from '../../../../../shared-web/track';
import { navigate } from '../router';
import { PageLoading } from '../components/PageLoading';
import { SectionEmpty } from '../components/SectionEmpty';
import { LetterCover } from '../components/LetterCover';
import { letterFirstLine } from '../letter-format';
import './Letters.css';

/**
 * Letters (§4.20 third tab, 2026-09-23 拍板; covers 二次拍板): the letters
 * Ember wrote this person, newest first, each as its cover — the same cover
 * the conversation shows when a letter arrives — in the Your Embers card
 * family. Once a week Ember reads the Embers they wrote since its last letter
 * (2026-10-31) and writes one letter when it has seen something; weeks it
 * wrote nothing are not cards.
 * Opening a cover opens the letter's page, which is the read (edge read_at);
 * until then the cover carries the dot.
 */
export function LettersPage(): ReactElement {
  const [letters, setLetters] = useState<WeeklyLetter[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    emberApi.meLetters()
      .then((r) => { if (alive) setLetters(r.letters); })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isAuthRequired(err)) navigate('/auth?returnTo=%2Fme%2Fletters', { replace: true });
        else {
          setFailed(true);
          trackFailed('me_letters_load', 'load_failed');
        }
      });
    return () => { alive = false; };
  }, []);

  if (failed) return <p className="ember-page-quiet">Couldn’t load your letters. Refresh to try again.</p>;
  if (!letters) return <PageLoading />;
  if (letters.length === 0) {
    return (
      <SectionEmpty icon="ember">
        Once a week, Ember reads your new Embers and writes you a letter, and draws your path — when it has seen something worth saying. Nothing here yet.
      </SectionEmpty>
    );
  }

  return (
    <div className="letters">
      <p className="letters-lede">Once a week, Ember reads your new Embers and may write you a letter, and draw your path.</p>
      <ul className="letters-cards" aria-label="Letters from Ember">
        {letters.map((l) => (
          <li key={l.id} className="letters-card">
            <LetterCover
              to={`/me/letters/${encodeURIComponent(l.id)}`}
              writtenAt={l.written_at}
              firstLine={letterFirstLine(l.letter)}
              ordinal={l.ordinal}
              readsFrom={l.reads_from}
              read={Boolean(l.read_at)}
              log="me_letter_open"
              letterId={l.id}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
